import React from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  BookOpen, Briefcase, TrendingUp, Cpu, Shield, Palette, LineChart, Cog,
  Stethoscope, Smile, Pill, HeartPulse, Scale, Building2, Sprout, Utensils,
  Newspaper, Atom, GraduationCap, Music, Plane, PawPrint,
  Library, Accessibility, Trophy, Wrench,
  ArrowLeft, GraduationCap as Cap, Sparkles, Target, FileText, ExternalLink, MapPin, Download,
} from "lucide-react";
import { getVertical } from "../data/verticals";

const ICONS = {
  BookOpen, Briefcase, TrendingUp, Cpu, Shield, Palette, LineChart, Cog,
  Stethoscope, Smile, Pill, HeartPulse, Scale, Building2, Sprout, Utensils,
  Newspaper, Atom, GraduationCap, Music, Plane, PawPrint,
  Library, Accessibility, Trophy, Wrench,
};

const Section = ({ icon: Icon, title, children, id }) => (
  <section className="bg-white rounded-2xl p-6 lg:p-8 cc-shadow-card" data-testid={`section-${id}`}>
    <div className="flex items-center gap-2.5 mb-5">
      <div className="w-9 h-9 rounded-lg bg-[#F58220]/10 grid place-items-center text-[#F58220]">
        <Icon size={18} />
      </div>
      <h2 className="font-head text-lg font-bold text-[#16244F]">{title}</h2>
    </div>
    {children}
  </section>
);

export default function VerticalDetail() {
  const { slug } = useParams();
  const v = getVertical(slug);

  if (!v || v.comingSoon) {
    return (
      <div className="min-h-screen bg-[#f7f8fb]">
        <Header />
        <main className="max-w-[900px] mx-auto px-5 py-24 text-center">
          <h1 className="font-head text-3xl font-bold text-[#16244F]">
            {v ? `${v.name} — Coming Soon` : "Vertical not found"}
          </h1>
          <p className="text-[#8a90a0] mt-3">
            {v ? "This vertical's detailed guide is being added shortly." : "The page you're looking for doesn't exist."}
          </p>
          <Link to="/higher-education" className="inline-flex items-center gap-2 mt-7 font-head font-semibold text-[#F58220]" data-testid="back-to-verticals">
            <ArrowLeft size={18} /> Back to all verticals
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const Icon = ICONS[v.icon] || BookOpen;

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Header />
      <main>
        <section className="bg-[#16244F] text-white print-hero">
          <div className="max-w-[1100px] mx-auto px-5 py-12 lg:py-16">
            <Link to="/higher-education" className="no-print inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#F58220] transition-colors mb-6" data-testid="back-link">
              <ArrowLeft size={16} /> All 22 Verticals
            </Link>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#F58220] grid place-items-center text-white shrink-0">
                <Icon size={28} />
              </div>
              <div className="flex-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#F58220]">{v.category}</span>
                <h1 className="font-head text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] mt-1">{v.name}</h1>
              </div>
              <button
                onClick={() => window.print()}
                data-testid="download-guide-btn"
                className="no-print shrink-0 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/25 text-white font-head font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"
              >
                <Download size={16} /> Download guide
              </button>
            </div>
            <p className="text-base md:text-lg text-white/75 mt-6 max-w-3xl leading-relaxed">{v.overview}</p>
          </div>
        </section>

        <div className="max-w-[1100px] mx-auto px-5 py-10 space-y-6">
          <Section icon={Cap} title="Available Degrees" id="degrees">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#8a90a0] border-b border-gray-100">
                    <th className="py-2.5 pr-4 font-semibold">Degree</th>
                    <th className="py-2.5 pr-4 font-semibold whitespace-nowrap">Duration</th>
                    <th className="py-2.5 font-semibold">Eligibility</th>
                  </tr>
                </thead>
                <tbody>
                  {v.degrees.map((d, i) => (
                    <tr key={i} className="border-b border-gray-50" data-testid={`degree-row-${i}`}>
                      <td className="py-3 pr-4 font-medium text-[#16244F]">{d.name}</td>
                      <td className="py-3 pr-4 text-[#45506b] whitespace-nowrap">{d.duration}</td>
                      <td className="py-3 text-[#45506b]">{d.eligibility}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <div className="grid lg:grid-cols-2 gap-6">
            <Section icon={Sparkles} title="Specializations" id="specializations">
              <div className="flex flex-wrap gap-2">
                {v.specializations.map((s, i) => (
                  <span key={i} className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#16244F]/5 text-[#16244F]">{s}</span>
                ))}
              </div>
            </Section>

            <Section icon={Target} title="Career Prospects" id="careers">
              <ul className="space-y-2.5">
                {v.careers.map((cp, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#45506b]">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F58220] shrink-0" />
                    {cp}
                  </li>
                ))}
              </ul>
            </Section>
          </div>

          <Section icon={FileText} title="Entrance Exams (after 12th)" id="exams">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {v.exams.map((e, i) => (
                <a
                  key={i}
                  href={e.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`exam-link-${i}`}
                  className="group flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-gray-100 hover:border-[#F58220]/40 hover:bg-[#F58220]/5 transition-colors"
                >
                  <span className="text-sm font-medium text-[#16244F]">{e.name}</span>
                  <ExternalLink size={14} className="text-[#c3c8d4] group-hover:text-[#F58220] shrink-0" />
                </a>
              ))}
            </div>
          </Section>

          <Section icon={Building2} title={`Top Colleges (${v.colleges.length})`} id="colleges">
            <div className="divide-y divide-gray-50">
              {v.colleges.map((col, i) => (
                <a
                  key={i}
                  href={col.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`college-link-${i}`}
                  className="group flex items-center gap-4 py-3.5 hover:bg-[#f7f8fb] -mx-2 px-2 rounded-lg transition-colors"
                >
                  <span className="w-8 h-8 rounded-lg bg-[#16244F]/5 grid place-items-center text-xs font-bold text-[#16244F] shrink-0">{col.rank}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-head font-semibold text-[#16244F] text-sm group-hover:text-[#F58220] transition-colors">{col.name}</span>
                      {col.tag && <span className="text-[10px] font-semibold uppercase tracking-wide text-[#F58220] bg-[#F58220]/10 px-2 py-0.5 rounded">{col.tag}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-[#8a90a0]">
                      {col.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {col.location}</span>}
                      <span>{col.admission}</span>
                    </div>
                  </div>
                  <ExternalLink size={15} className="text-[#c3c8d4] group-hover:text-[#F58220] shrink-0" />
                </a>
              ))}
            </div>
          </Section>

          <div className="no-print bg-[#16244F] rounded-2xl p-7 lg:p-9 text-center">
            <h3 className="font-head text-xl font-bold text-white">Not sure if {v.name} is right for you?</h3>
            <p className="text-white/70 text-sm mt-2 max-w-xl mx-auto">Take our free psychometric assessment or book a session with a counsellor to find your best-fit stream.</p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <Link to="/assessment" className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold text-sm px-5 py-3 rounded-lg transition-colors" data-testid="cta-assessment">
                <Sparkles size={16} /> Free Assessment
              </Link>
              <Link to="/counsellors" className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white font-head font-semibold text-sm px-5 py-3 rounded-lg transition-colors" data-testid="cta-counsellors">
                Find a Counsellor
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
